import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { api, fetchGuardiansCount } from '../../lib/api';

/**
 * OpenLedgerReceipt — § V of the Home manifesto.
 *
 * A visual paper receipt rendered entirely in monospace. Every figure
 * is fetched live from the server (qc/aggregate, guardians/count,
 * admin/impact). No vanity numbers, no marketing claims — the page
 * itself becomes the proof.
 *
 * The receipt feels physical: serrated top edge, narrow ticket width,
 * cream paper background, faint grain, a single perforated divider.
 */

const fmt = (n, digits = 0) =>
  Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const Row = ({ k, v, accent, testId }) => (
  <li
    data-testid={testId}
    className={`flex items-baseline justify-between gap-4 py-1 ${accent ? 'text-[#3C5260]' : 'text-[#3C5260]/80'}`}
  >
    <span className="font-mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase truncate">
      {k}
    </span>
    <span
      aria-hidden
      className="flex-1 border-b border-dotted border-[#3C5260]/30 mx-2 translate-y-[-3px]"
    />
    <span
      className={`font-mono text-[12px] md:text-[14px] tracking-[0.04em] tabular-nums ${accent ? 'font-semibold' : ''}`}
    >
      {v}
    </span>
  </li>
);

const OpenLedgerReceipt = ({ testId = 'home-open-ledger' }) => {
  const [data, setData] = useState({ guardians: null, qc: null, impact: null });

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      fetchGuardiansCount(),
      api.get('/qc/aggregate').then((r) => r.data),
      api.get('/admin/impact').then((r) => r.data),
    ]).then(([g, q, i]) => {
      if (!alive) return;
      setData({
        guardians: g.status === 'fulfilled' ? g.value : null,
        qc:        q.status === 'fulfilled' ? q.value : null,
        impact:    i.status === 'fulfilled' ? i.value : null,
      });
    });
    return () => { alive = false; };
  }, []);

  const now = new Date();
  const ts = now.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  const txid = `0x${Math.abs(now.getTime() * 7).toString(16).slice(0, 12)}…`;

  return (
    <section
      data-testid={testId}
      aria-label="The Open Ledger — the page itself, the proof"
      className="relative w-full"
      style={{ backgroundColor: '#F4F8F9' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        {/* LEFT — section heading */}
        <header className="md:col-span-5">
          <p
            data-testid={`${testId}-overline`}
            className="font-mono text-[10px] md:text-[12px] tracking-[0.42em] uppercase font-semibold text-[#7C9CB1] mb-6 flex items-center gap-3"
          >
            <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
            The Open Ledger · § V
          </p>
          <h2
            className="font-serif text-[#3C5260] font-light leading-[0.96] tracking-[-0.01em]"
            style={{ fontSize: 'clamp(40px, 5vw, 80px)' }}
          >
            The page itself.
            <br />
            <em className="text-[#7C9CB1]">The proof.</em>
          </h2>
          <p className="mt-6 font-serif italic text-lg md:text-xl text-[#7C9CB1]/90 leading-relaxed max-w-md">
            No marketing claims. Every figure below is read from the
            server at the moment you look — refills witnessed, batches
            certified, Guardians inscribed, plastic averted. The receipt
            is the truth.
          </p>
          <a
            href="/transparency"
            data-testid={`${testId}-cta`}
            className="group mt-8 inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase text-[#7C9CB1] hover:text-[#3C5260] transition-colors"
          >
            See the full ledger
            <ArrowRight size={12} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        </header>

        {/* RIGHT — the receipt */}
        <aside className="md:col-span-7">
          <div
            data-testid={`${testId}-paper`}
            className="relative bg-[#FAF6EC] text-[#3C5260] mx-auto max-w-[520px] shadow-[0_30px_60px_-30px_rgba(60,82,96,0.35)]"
            style={{
              backgroundImage:
                'radial-gradient(rgba(60,82,96,0.04) 1px, transparent 1px)',
              backgroundSize: '3px 3px',
            }}
          >
            {/* Serrated top edge */}
            <span
              aria-hidden
              className="block w-full h-3"
              style={{
                background:
                  "linear-gradient(135deg, transparent 33.333%, #FAF6EC 33.333%) -10px 0, linear-gradient(225deg, transparent 33.333%, #FAF6EC 33.333%) -10px 0",
                backgroundColor: 'transparent',
                backgroundSize: '20px 20px',
              }}
            />

            <div className="px-8 md:px-10 pt-6 pb-9">
              {/* Receipt header */}
              <p className="text-center font-mono text-[11px] tracking-[0.4em] uppercase font-bold text-[#3C5260]">
                Shreesavaa · Atelier
              </p>
              <p className="mt-1 text-center font-mono text-[9px] tracking-[0.34em] uppercase text-[#3C5260]/70">
                Heavener, Oklahoma · USA
              </p>
              <p className="mt-1 text-center font-mono text-[9px] tracking-[0.34em] uppercase text-[#3C5260]/70">
                Open Ledger · {ts}
              </p>

              {/* Perforation */}
              <div
                aria-hidden
                className="my-6 border-t border-dashed border-[#3C5260]/35 relative"
              >
                <span className="absolute -top-2 -left-3 w-4 h-4 rounded-full bg-[#F4F8F9] border-r border-b border-[#3C5260]/15" />
                <span className="absolute -top-2 -right-3 w-4 h-4 rounded-full bg-[#F4F8F9] border-l border-b border-[#3C5260]/15" />
              </div>

              {/* THE CIRCLE */}
              <p
                data-testid={`${testId}-section-circle`}
                className="font-mono text-[9px] tracking-[0.36em] uppercase text-[#7C9CB1] mb-2"
              >
                The Circle
              </p>
              <ul className="mb-6">
                <Row
                  k="Guardians inscribed"
                  v={fmt(data.guardians?.total_registered)}
                  accent
                  testId={`${testId}-guardians-total`}
                />
                <Row
                  k="Founding cap"
                  v={fmt(data.guardians?.founding_cap ?? 1000)}
                />
                <Row
                  k="Seats remaining"
                  v={fmt(data.guardians?.seats_remaining ?? 1000)}
                />
              </ul>

              {/* PURITY */}
              <p
                data-testid={`${testId}-section-purity`}
                className="font-mono text-[9px] tracking-[0.36em] uppercase text-[#7C9CB1] mb-2"
              >
                Purity
              </p>
              <ul className="mb-6">
                <Row
                  k="Certified batches"
                  v={`${fmt(data.qc?.certified_batches)} / ${fmt(data.qc?.total_batches)}`}
                  accent
                  testId={`${testId}-qc-certified`}
                />
                <Row
                  k="Certification rate"
                  v={`${fmt(data.qc?.certification_rate_pct, 2)} %`}
                />
                <Row
                  k="Avg pH"
                  v={fmt(data.qc?.ph_average, 2)}
                />
                <Row
                  k="Avg free alkali"
                  v={`${fmt((data.qc?.free_alkali_average_pct ?? 0) * 100, 4)} ppt`}
                />
                <Row
                  k="Microbial avg"
                  v={`${fmt(data.qc?.microbial_average_cfu_per_g, 0)} CFU/g`}
                />
              </ul>

              {/* IMPACT */}
              <p
                data-testid={`${testId}-section-impact`}
                className="font-mono text-[9px] tracking-[0.36em] uppercase text-[#7C9CB1] mb-2"
              >
                Impact
              </p>
              <ul className="mb-6">
                <Row
                  k="Water · sewer-positive"
                  v={`${fmt(data.impact?.water_stewardship?.liters_sewer_positive, 3)} L`}
                  accent
                  testId={`${testId}-impact-water`}
                />
                <Row
                  k="Plastic averted"
                  v={`${fmt(data.impact?.plastic_burden?.plastic_avoided_grams)} g`}
                  testId={`${testId}-impact-plastic`}
                />
                <Row
                  k="Permanent glass"
                  v={`${fmt(data.impact?.plastic_burden?.permanent_glass_count)} vessels`}
                />
                <Row
                  k="SEVA in motion"
                  v={fmt(data.impact?.seva_velocity?.total_in_motion, 2)}
                  testId={`${testId}-impact-seva`}
                />
              </ul>

              {/* Footer perf */}
              <div
                aria-hidden
                className="my-6 border-t border-dashed border-[#3C5260]/35 relative"
              />

              {/* Footer total + tx */}
              <ul>
                <Row
                  k="Vessel · binary truth"
                  v="251 ml"
                  accent
                />
                <Row
                  k="Production unit cost"
                  v="$ 8.30"
                  accent
                />
                <Row
                  k="Anchor"
                  v="Base Sepolia"
                />
              </ul>

              <p
                data-testid={`${testId}-txid`}
                className="mt-7 text-center font-mono text-[10px] tracking-[0.18em] text-[#3C5260]/80"
              >
                tx · {txid}
              </p>
              <p className="mt-1 text-center font-mono text-[9px] tracking-[0.36em] uppercase text-[#7C9CB1]">
                Recorded, not recited
              </p>
            </div>

            {/* Serrated bottom edge */}
            <span
              aria-hidden
              className="block w-full h-3"
              style={{
                background:
                  "linear-gradient(45deg, transparent 33.333%, #FAF6EC 33.333%) -10px 0, linear-gradient(-45deg, transparent 33.333%, #FAF6EC 33.333%) -10px 0",
                backgroundColor: 'transparent',
                backgroundSize: '20px 20px',
              }}
            />
          </div>
        </aside>
      </div>
    </section>
  );
};

export default OpenLedgerReceipt;
